// Solomon Portal v2.x — /support/new route.
//
// Renders the in-portal new-conversation composer. On successful send,
// navigates to /support/<new-number> so the user lands on their newly
// created conversation in the same surface as any other read.
//
// Spec: SPEC-reply-composer.md §2.2.

import * as React from 'react';
import { useNavigate, Link } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import {
  ReplyComposer,
  clearDraft,
  type ComposerErrorReason,
  type ComposerSubmitPayload,
} from '../components/support/ReplyComposer';
import { postConversation, type PostConversationResult } from '../lib/post-conversation-api';

function mapResultToError(result: PostConversationResult): ComposerErrorReason {
  switch (result.kind) {
    case 'rejected':
      // PostConversationRejectionReason is a strict subset of
      // ComposerErrorReason (verified by inspection of both type
      // definitions), so the assignment is type-safe without an
      // assertion.
      return result.reason;
    case 'unauthenticated':
      return 'unauthenticated';
    case 'forbidden':
      return 'forbidden';
    case 'upstream_unavailable':
      return 'upstream_unavailable';
    case 'config_error':
      return 'config_error';
    case 'network_error':
      return 'network_error';
    case 'aborted':
      return 'unknown';
    case 'ok':
      return 'unknown';
    default: {
      const _exhaustive: never = result;
      void _exhaustive;
      return 'unknown';
    }
  }
}

export function NewConversationRoute(): React.ReactNode {
  const navigate = useNavigate();
  const [inFlight, setInFlight] = React.useState(false);
  const [error, setError] = React.useState<ComposerErrorReason | null>(null);

  const handleCancel = (): void => {
    clearDraft({ kind: 'new' });
    void navigate('/support', { replace: true });
  };

  const handleSubmit = async (payload: ComposerSubmitPayload): Promise<void> => {
    if (typeof payload.subject !== 'string') {
      setError('subject_required');
      return;
    }
    setInFlight(true);
    setError(null);
    let result: PostConversationResult;
    try {
      result = await postConversation({
        subject: payload.subject,
        text: payload.text,
        attachmentIds: payload.attachmentIds,
        idempotencyKey: payload.idempotencyKey,
      });
    } finally {
      setInFlight(false);
    }
    if (result.kind === 'ok') {
      clearDraft({ kind: 'new' });
      // Navigate to the new conversation. Fall back to /support if the
      // upstream didn't return a number for some reason (rare but
      // documented in the helper).
      if (typeof result.response.conversationNumber === 'number') {
        void navigate(`/support/${String(result.response.conversationNumber)}`, {
          replace: true,
        });
      } else {
        void navigate('/support', { replace: true });
      }
      return;
    }
    setError(mapResultToError(result));
  };

  return (
    <article aria-labelledby="new-conversation-title" className="flex max-w-4xl flex-col gap-6">
      <Link
        to="/support"
        className="inline-flex items-center gap-1 text-[13px] font-medium text-text-muted hover:text-orange"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
        <span>Back to support</span>
      </Link>

      <header className="flex flex-col gap-2 border-b border-border-light pb-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-orange">New conversation</p>
        <h1 id="new-conversation-title">Start a new conversation</h1>
        <p className="text-[13px] text-text-muted">
          Reach out to Solomon Advising about anything &mdash; we&rsquo;ll respond by email.
        </p>
      </header>

      <ReplyComposer
        mode={{ kind: 'new' }}
        onCancel={handleCancel}
        onSubmit={handleSubmit}
        externalError={error}
        inFlight={inFlight}
      />
    </article>
  );
}
